import React, { useState, useEffect, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { Clock, X, Timer } from 'lucide-react'

interface LessonDeadlineProps {
  lessonId: string
  onExpire?: () => void
}

const PRESETS = [
  { label: '1h', minutes: 60 },
  { label: '1.5h', minutes: 90 },
  { label: '2h', minutes: 120 },
  { label: '3h', minutes: 180 },
]

function getStorageKey(lessonId: string) {
  return `lesson_deadline_${lessonId}`
}

export function LessonDeadline({ lessonId, onExpire }: LessonDeadlineProps) {
  const [deadline, setDeadline] = useState<number | null>(null)
  const [remaining, setRemaining] = useState<number>(0)
  const [customMinutes, setCustomMinutes] = useState('')
  const expiredRef = useRef(false)

  // Load deadline from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(getStorageKey(lessonId))
    if (stored) {
      const ts = parseInt(stored, 10)
      if (ts > Date.now()) {
        setDeadline(ts)
      } else {
        // Expired — keep it to show "Time's up!" until cleared
        setDeadline(ts)
      }
    }
  }, [lessonId])

  // Reset expired flag when deadline changes
  useEffect(() => {
    expiredRef.current = false
  }, [deadline])

  // Countdown tick
  useEffect(() => {
    if (!deadline) return

    const tick = () => {
      const diff = Math.max(0, deadline - Date.now())
      const secs = Math.floor(diff / 1000)
      setRemaining(secs)

      if (secs <= 0 && !expiredRef.current) {
        expiredRef.current = true
        onExpire?.()
      }
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [deadline, onExpire])

  const setDuration = useCallback((minutes: number) => {
    const ts = Date.now() + minutes * 60 * 1000
    localStorage.setItem(getStorageKey(lessonId), String(ts))
    setDeadline(ts)
  }, [lessonId])

  const handleCustomSet = () => {
    const hours = parseFloat(customMinutes)
    if (!hours || hours <= 0) return
    setDuration(hours * 60)
    setCustomMinutes('')
  }

  const handleClear = () => {
    localStorage.removeItem(getStorageKey(lessonId))
    setDeadline(null)
    setRemaining(0)
  }

  const formatTime = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = totalSeconds % 60
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  const getTimerColor = () => {
    if (remaining <= 0) return 'text-red-400'
    if (remaining <= 60) return 'text-red-400'
    if (remaining <= 300) return 'text-red-400'
    if (remaining <= 600) return 'text-yellow-400'
    return 'text-green-400'
  }

  const isExpired = deadline !== null && remaining <= 0

  // No deadline set yet — show preset picker
  if (!deadline) {
    return (
      <div className="bg-surface-deep rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Timer className="w-5 h-5 text-[var(--color-primary)]" />
          <h3 className="text-lg font-semibold">Lesson Deadline</h3>
        </div>

        <p className="text-sm text-theme-text-muted mb-4">Set a deadline for this self-paced lesson.</p>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => setDuration(p.minutes)}
              className="px-3 py-2 bg-surface-hover text-theme-text rounded-lg hover:bg-surface-hover transition-colors font-medium text-sm"
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="number"
            value={customMinutes}
            onChange={(e) => setCustomMinutes(e.target.value)}
            placeholder="Hours"
            min="0.5"
            step="0.5"
            className="flex-1 px-3 py-2 bg-surface-hover border border-theme-border rounded-lg text-theme-text text-sm placeholder-theme-muted focus:outline-none focus:border-[var(--color-primary)]"
          />
          <button
            onClick={handleCustomSet}
            disabled={!customMinutes || parseFloat(customMinutes) <= 0}
            title="Set deadline in hours"
            className="ds-btn ds-btn-primary ds-btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Set
          </button>
        </div>
      </div>
    )
  }

  // Deadline is set — show countdown
  return (
    <div className="bg-surface-deep rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Timer className="w-5 h-5 text-[var(--color-primary)]" />
          <h3 className="text-lg font-semibold">Lesson Deadline</h3>
        </div>
        <button
          onClick={handleClear}
          className="p-1.5 text-theme-text-muted hover:text-red-400 transition-colors"
          title="Clear deadline"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="text-center">
        {isExpired ? (
          <motion.div
            className="text-3xl font-bold text-red-400"
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            Time's up!
          </motion.div>
        ) : (
          <motion.div
            className={`text-4xl font-mono font-bold ${getTimerColor()}`}
            animate={{
              scale: remaining <= 60 ? [1, 1.05, 1] : 1,
            }}
            transition={{
              duration: 1,
              repeat: remaining <= 60 ? Infinity : 0,
            }}
          >
            {formatTime(remaining)}
          </motion.div>
        )}

        <div className="text-sm text-theme-text-muted mt-2">
          Ends at {new Date(deadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  )
}
